/**
 * BrowserSelect — radio control for picking the Playwright browser
 * engine (#459, polished in #994).
 *
 * Three changes from the original implementation:
 *
 *   1. **Brand icons.** lucide's monochrome `Chrome` / `Flame` / `Globe`
 *      glyphs were replaced with hand-rolled inline-SVG brand marks
 *      (see `icons/BrowserBrandIcons.tsx`). Operators recognise tests
 *      by brand mark before they read the label — the prior icons
 *      gave them nothing to latch onto.
 *
 *   2. **Per-option caption naming the proxy browsers.** Each engine
 *      runs more than one shipping browser at runtime; the caption
 *      tells the operator which products they're effectively
 *      validating. The mapping is verbatim from Playwright's engine-
 *      to-browser table and is content the QA team needs visible at
 *      decision time, not hidden in a tooltip.
 *
 *   3. **Equal-width horizontal row.** The three options sit side-
 *      by-side (matching the dialog's space-efficient inline-control
 *      rhythm). Inside each option, the icon stacks above the label
 *      and the caption wraps to a second line where needed; this
 *      keeps the proxy-browser list visible at the dialog's
 *      `max-w-lg` width without ellipsis-truncating. Briefly
 *      switched to a vertical stack in #994 then reverted to
 *      horizontal in the same PR series after operator feedback.
 *
 * Styled to match the Checkbox pattern (hand-rolled native input +
 * Tailwind) rather than introducing a radix dependency the rest of
 * the UI library doesn't use.
 */
import { useId } from 'react'
import type { ComponentType, SVGProps } from 'react'
import { cn } from '@/lib/utils'
import {
  ChromiumBrandIcon,
  FirefoxBrandIcon,
  WebKitBrandIcon,
} from './icons/BrowserBrandIcons'

export type BrowserOption = 'chromium' | 'firefox' | 'webkit'

interface BrowserSelectProps {
  value: BrowserOption
  onChange: (browser: BrowserOption) => void
  disabled?: boolean
  className?: string
  /** Accessible name for the radiogroup. Provide one of `ariaLabel` or
   * `ariaLabelledby` so screen readers can announce what the group
   * controls. Either is acceptable; `ariaLabelledby` is preferred when
   * a visible `<Label>` already exists alongside the control. */
  ariaLabel?: string
  ariaLabelledby?: string
}

/** Per-option metadata. `caption` names the shipping browsers each
 * engine acts as a proxy for at runtime — `chromium` covers the
 * Blink-engine browsers shown in the caption (Chrome / Edge / Opera
 * / Brave); `firefox` is its own engine (Gecko) and only proxies for
 * itself; `webkit` covers Safari on macOS and iOS. Mapping is taken
 * from Playwright's engine table and trimmed to the four operators
 * recognize at a glance — keep this comment in sync with the
 * `caption` strings so a future reader doesn't think the list drifted
 * (PR #1030 Copilot review). */
interface BrowserOptionMeta {
  value: BrowserOption
  label: string
  caption: string
  /** Component renders an inline SVG; matches the shape of the three
   * brand icons in `icons/BrowserBrandIcons.tsx` (each accepts the
   * standard SVG props, returns a `<svg>`). Typed via React's
   * `ComponentType<SVGProps>` so the JSX namespace isn't a build
   * dependency (React 19 dropped the global). */
  Icon: ComponentType<SVGProps<SVGSVGElement>>
}

const OPTIONS: ReadonlyArray<BrowserOptionMeta> = [
  {
    value: 'chromium',
    label: 'Chromium',
    caption: 'Chrome, Edge, Opera, Brave',
    Icon: ChromiumBrandIcon,
  },
  {
    value: 'firefox',
    label: 'Firefox',
    caption: 'Firefox',
    Icon: FirefoxBrandIcon,
  },
  {
    value: 'webkit',
    label: 'WebKit',
    caption: 'Safari (desktop + iOS)',
    Icon: WebKitBrandIcon,
  },
]

export function BrowserSelect({ value, onChange, disabled, className, ariaLabel, ariaLabelledby }: BrowserSelectProps) {
  // Per-instance radio-group name. The original implementation
  // hardcoded `name="browser-select"`; if two BrowserSelect mounts
  // existed on the same page (the header note about future reuse
  // for the Authoring template makes this plausible), their radio
  // groups would conflict — selecting in one would deselect the
  // other (PR #1030 Copilot review).
  const groupName = useId()
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      className={cn('flex gap-2', className)}
    >
      {OPTIONS.map(({ value: optionValue, label, caption, Icon }) => {
        const checked = value === optionValue
        return (
          <label
            key={optionValue}
            className={cn(
              // `flex-1` gives each option an equal share of the
              // row's width — the radiogroup parent is `flex gap-2`,
              // and three equal-flex children divide the remaining
              // space evenly. `min-w-0` is mandatory: without it the
              // caption span's content forbids the flex item from
              // shrinking below its intrinsic width, defeating
              // `flex-1` and pushing the third option off-row at
              // narrow widths.
              'flex flex-1 min-w-0 flex-col items-center gap-1 text-center rounded-md border px-2 py-2 cursor-pointer transition-colors',
              'hover:bg-accent hover:text-accent-foreground',
              checked
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-input bg-background text-foreground',
              disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            <input
              type="radio"
              name={groupName}
              value={optionValue}
              checked={checked}
              onChange={() => onChange(optionValue)}
              disabled={disabled}
              className="sr-only"
            />
            {/* Brand icon stays full-color whether the radio is
                selected or not — recognition is the point. */}
            <Icon className="h-6 w-6 shrink-0" />
            <span className="text-sm leading-tight">{label}</span>
            {/* Caption wraps to a second line at narrow widths
                rather than ellipsis-truncating — the proxy-browser
                list is more useful as a wrapped two-line phrase
                than as "Chrome, …" truncation. Muted-foreground
                base; primary/80 when selected so it stays legible
                on the primary-tinted background. */}
            <span
              className={cn(
                'text-[10px] leading-snug',
                checked ? 'text-primary/80' : 'text-muted-foreground',
              )}
            >
              {caption}
            </span>
          </label>
        )
      })}
    </div>
  )
}
